import React, { useEffect, useMemo, useRef, useState } from 'react';
import useElementToPdf from '../utils/useElementToPdf';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Edit3,
  TrendingUp,
  TrendingDown,
  Check,
  X,
  Printer,
  FileText,
  Trash2,
  Loader2,
} from 'lucide-react';

// ---------- Modal: barber/admin submits a new price change request ----------
export const PriceChangeRequestModal = ({ open, onClose, service, onSubmitted, isArabic, redirectAfterSave = true }) => {
  const navigate = useNavigate();
  const [newPrice, setNewPrice] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && service) {
      setNewPrice('');
      setReason('');
    }
  }, [open, service]);

  if (!open || !service) return null;

  const oldPrice = Number(service.price || 0);
  const np = parseFloat(newPrice);
  const validPrice = !Number.isNaN(np) && np >= 0 && Math.abs(np - oldPrice) > 0.0001;
  const pct = validPrice && oldPrice > 0 ? ((np - oldPrice) / oldPrice) * 100 : null;
  const direction = pct === null ? null : pct > 0 ? 'increase' : 'decrease';

  const submit = async () => {
    if (!validPrice) {
      toast.error(isArabic ? 'يرجى إدخال سعر جديد مختلف عن الحالي' : 'Please enter a valid new price');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/services/price-changes', {
        service_id: service.id,
        new_price: np,
        reason: reason || '',
      });
      const status = res.data?.request?.status;
      const successMsg = status === 'approved'
        ? (isArabic ? 'تم حفظ التعديل وتطبيق السعر الجديد ✓' : 'Saved and applied successfully ✓')
        : (isArabic
            ? 'تم حفظ طلبك وإرساله للمدير للاعتماد ✓'
            : 'Saved and sent to manager for approval ✓');
      toast.success(successMsg, { duration: 4000 });
      onSubmitted?.();
      onClose?.();
      // Redirect barber back to the home (dashboard) after successful save
      if (redirectAfterSave) {
        setTimeout(() => navigate('/'), 600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل إرسال الطلب' : 'Failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="price-change-modal">
      <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full space-y-4" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="flex items-center gap-2">
          <Edit3 className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-bold">
            {isArabic ? 'طلب تعديل السعر' : 'Price Change Request'}
          </h3>
        </div>

        <div className="bg-secondary/40 rounded-lg p-3 text-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{isArabic ? 'الخدمة' : 'Service'}</span>
            <span className="font-semibold">
              {isArabic ? service.name_ar : service.name_en} {isArabic ? `/ ${service.name_en}` : `/ ${service.name_ar}`}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{isArabic ? 'السعر الحالي' : 'Current Price'}</span>
            <span className="font-bold">{oldPrice.toFixed(2)} {isArabic ? 'د.إ' : 'AED'}</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {isArabic ? 'السعر الجديد المقترح' : 'Proposed New Price'}
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            data-testid="new-price-input"
            className="bg-secondary/50 border-white/10"
          />
          {pct !== null && (
            <div className={`mt-2 text-sm flex items-center gap-1 ${direction === 'increase' ? 'text-emerald-500' : 'text-orange-500'}`}>
              {direction === 'increase' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {direction === 'increase'
                ? (isArabic ? `زيادة بنسبة ${pct.toFixed(2)}%` : `Increase of ${pct.toFixed(2)}%`)
                : (isArabic ? `خصم بنسبة ${Math.abs(pct).toFixed(2)}%` : `Discount of ${Math.abs(pct).toFixed(2)}%`)}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {isArabic ? 'السبب (اختياري)' : 'Reason (optional)'}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="price-change-reason"
            placeholder={isArabic ? 'مثال: تغير أسعار المنتجات في السوق' : 'e.g. market price changes'}
            className="w-full rounded-md bg-secondary/50 border border-white/10 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {isArabic ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={submit} disabled={submitting || !validPrice} data-testid="submit-price-change-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isArabic ? 'إرسال' : 'Submit')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------- Bilingual A4 print sheet ----------
const PrintSheet = React.forwardRef(({ rows, salonName = 'KE Gents Saloon' }, ref) => {
  const today = new Date().toLocaleDateString();
  return (
    <div ref={ref} style={{ padding: '14mm', fontFamily: 'Cairo, Tajawal, Arial, sans-serif', color: '#111', background: '#fff', width: '210mm', minHeight: '297mm', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>{salonName}</h1>
        <h2 style={{ margin: '4px 0 0', fontSize: 14 }}>قائمة تغييرات الأسعار / Price Changes List</h2>
        <div style={{ fontSize: 10, color: '#555' }}>{today}</div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: '#f3f3f3' }}>
            <th style={th}>#</th>
            <th style={th}>الخدمة<br /><span style={{ fontWeight: 400 }}>Service</span></th>
            <th style={th}>السعر القديم<br /><span style={{ fontWeight: 400 }}>Old Price</span></th>
            <th style={th}>السعر الجديد<br /><span style={{ fontWeight: 400 }}>New Price</span></th>
            <th style={th}>التغيير<br /><span style={{ fontWeight: 400 }}>Change</span></th>
            <th style={th}>الحالة<br /><span style={{ fontWeight: 400 }}>Status</span></th>
            <th style={th}>الطالب<br /><span style={{ fontWeight: 400 }}>Requested By</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#777' }}>لا توجد بيانات / No data</td></tr>
          ) : rows.map((r, i) => {
            const pct = Number(r.change_pct || 0);
            const isIncrease = pct >= 0;
            return (
              <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}>{i + 1}</td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{r.service_name_ar || '-'}</div>
                  <div style={{ fontSize: 10, color: '#555' }}>{r.service_name_en || '-'}</div>
                </td>
                <td style={{ ...td, textAlign: 'center' }}>{Number(r.old_price || 0).toFixed(2)}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{Number(r.new_price || 0).toFixed(2)}</td>
                <td style={{ ...td, textAlign: 'center', color: isIncrease ? '#15803d' : '#c2410c', fontWeight: 700 }}>
                  {isIncrease ? '▲' : '▼'} {Math.abs(pct).toFixed(2)}%
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 400 }}>
                    {isIncrease ? 'زيادة / Increase' : 'خصم / Discount'}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'center' }}>{statusLabel(r.status)}</td>
                <td style={td}>{r.requested_by_name || '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 16, fontSize: 10, color: '#555', display: 'flex', justifyContent: 'space-between' }}>
        <div>عدد الطلبات / Total: {rows.length}</div>
        <div>{salonName}</div>
      </div>
    </div>
  );
});
PrintSheet.displayName = 'PriceChangePrintSheet';

const th = { padding: 8, border: '1px solid #ddd', textAlign: 'right', fontWeight: 700 };
const td = { padding: 8, border: '1px solid #eee', textAlign: 'right' };
const statusLabel = (s) => {
  if (s === 'approved') return 'معتمد / Approved';
  if (s === 'rejected') return 'مرفوض / Rejected';
  return 'معلّق / Pending';
};

// ---------- Main panel: list + filter + print + admin approve/reject ----------
export const PriceChangeRequestsPanel = ({ isAdmin, isArabic, onChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [busyId, setBusyId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/services/price-changes');
      setRequests(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `price_changes_${new Date().toISOString().split('T')[0]}`,
    pageStyle: `
      @page { size: A4 portrait; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `,
  });

  const decide = async (id, approved) => {
    setBusyId(id);
    try {
      let rejection_reason = null;
      if (!approved) {
        rejection_reason = window.prompt(isArabic ? 'سبب الرفض (اختياري)' : 'Rejection reason (optional)') || '';
      }
      await api.post(`/services/price-changes/${id}/approve`, { approved, rejection_reason });
      toast.success(approved ? (isArabic ? 'تم الاعتماد' : 'Approved') : (isArabic ? 'تم الرفض' : 'Rejected'));
      await fetchRequests();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isArabic ? 'هل تريد حذف الطلب؟' : 'Delete the request?')) return;
    try {
      await api.delete(`/services/price-changes/${id}`);
      toast.success(isArabic ? 'تم الحذف' : 'Deleted');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  return (
    <div className="space-y-4" dir={isArabic ? 'rtl' : 'ltr'} data-testid="price-changes-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-bold">
            {isArabic ? 'طلبات تعديل الأسعار' : 'Price Change Requests'}
          </h3>
          <span className="text-xs text-muted-foreground">({requests.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'pending', 'approved', 'rejected'].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}-btn`}
            >
              {f === 'all' && (isArabic ? 'الكل' : 'All')}
              {f === 'pending' && (isArabic ? 'معلّق' : 'Pending')}
              {f === 'approved' && (isArabic ? 'معتمد' : 'Approved')}
              {f === 'rejected' && (isArabic ? 'مرفوض' : 'Rejected')}
            </Button>
          ))}
          {isAdmin && (
            <Button size="sm" onClick={() => setShowPreview(true)} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="open-print-preview-btn">
              <Printer className="w-4 h-4" />
              {isArabic ? 'تنزيل PDF' : 'Download PDF'}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
          {isArabic ? 'جاري التحميل…' : 'Loading…'}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground border border-dashed border-white/10 rounded-lg">
          {isArabic ? 'لا توجد طلبات' : 'No requests'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="price-changes-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-start py-2 px-2">{isArabic ? 'الخدمة' : 'Service'}</th>
                <th className="text-center py-2 px-2">{isArabic ? 'القديم' : 'Old'}</th>
                <th className="text-center py-2 px-2">{isArabic ? 'الجديد' : 'New'}</th>
                <th className="text-center py-2 px-2">{isArabic ? 'التغيير' : 'Change'}</th>
                <th className="text-start py-2 px-2">{isArabic ? 'الطالب' : 'Requested By'}</th>
                <th className="text-center py-2 px-2">{isArabic ? 'الحالة' : 'Status'}</th>
                <th className="text-center py-2 px-2">{isArabic ? 'إجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const pct = Number(r.change_pct || 0);
                const isIncrease = pct >= 0;
                return (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`pc-row-${r.id}`}>
                    <td className="py-2 px-2">
                      <div className="font-semibold">{isArabic ? r.service_name_ar : r.service_name_en}</div>
                      <div className="text-xs text-muted-foreground">{isArabic ? r.service_name_en : r.service_name_ar}</div>
                      {r.reason && <div className="text-xs text-muted-foreground mt-1">📝 {r.reason}</div>}
                    </td>
                    <td className="py-2 px-2 text-center">{Number(r.old_price || 0).toFixed(2)}</td>
                    <td className="py-2 px-2 text-center font-semibold">{Number(r.new_price || 0).toFixed(2)}</td>
                    <td className={`py-2 px-2 text-center font-bold ${isIncrease ? 'text-emerald-500' : 'text-orange-500'}`}>
                      {isIncrease ? '▲' : '▼'} {Math.abs(pct).toFixed(2)}%
                      <div className="text-[10px] font-normal text-muted-foreground">
                        {isIncrease ? (isArabic ? 'زيادة' : 'Increase') : (isArabic ? 'خصم' : 'Discount')}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <div className="text-sm">{r.requested_by_name}</div>
                      <div className="text-xs text-muted-foreground">{r.requested_by_role}</div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-500' :
                        r.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                        'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        {r.status === 'approved' && (isArabic ? 'معتمد' : 'Approved')}
                        {r.status === 'rejected' && (isArabic ? 'مرفوض' : 'Rejected')}
                        {r.status === 'pending' && (isArabic ? 'معلّق' : 'Pending')}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isAdmin && r.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-500 hover:text-emerald-600"
                              disabled={busyId === r.id}
                              onClick={() => decide(r.id, true)}
                              data-testid={`approve-pc-${r.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600"
                              disabled={busyId === r.id}
                              onClick={() => decide(r.id, false)}
                              data-testid={`reject-pc-${r.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(isAdmin || r.status === 'pending') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground hover:text-red-500"
                            onClick={() => remove(r.id)}
                            data-testid={`delete-pc-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {r.status === 'rejected' && r.rejection_reason && (
                        <div className="text-[10px] text-red-400 mt-1">⚠ {r.rejection_reason}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Print preview modal (admin only) */}
      {showPreview && isAdmin && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-auto" data-testid="print-preview-modal">
          <div className="bg-card border border-white/10 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-card border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-primary" />
                <h3 className="font-bold">
                  {isArabic ? 'معاينة الطباعة (ثنائية اللغة)' : 'Print Preview (Bilingual)'}
                </h3>
                <span className="text-xs text-muted-foreground">
                  ({filtered.length} {isArabic ? 'طلب' : 'rows'} • A4 portrait)
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  {isArabic ? 'إغلاق' : 'Close'}
                </Button>
                <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="confirm-print-btn">
                  <Printer className="w-4 h-4" />
                  {isArabic ? 'تنزيل PDF' : 'Download PDF'}
                </Button>
              </div>
            </div>
            <div className="p-4 bg-white flex justify-center">
              <PrintSheet ref={printRef} rows={filtered} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceChangeRequestsPanel;
